import React, {useCallback, useEffect, useMemo, useRef} from 'react';
import {Box, Stack, useTheme} from '@mui/material';
import {ActiveElement, ChartData, ChartEvent, type ChartOptions, DefaultDataPoint, InteractionMode, ScaleTypeRegistry, TooltipItem} from 'chart.js';
import {Bar} from 'react-chartjs-2';
import {Align, Anchor, TextAlign} from "chartjs-plugin-datalabels/types/options";
import {SearchDataSettingsOrderBy, SearchDataSettingsPaginationInfo} from "../../../../../../../../hooks/use-search";
import {Context} from "chartjs-plugin-datalabels";
import {DashboardWidgetLayoutConfig, DashboardWidgetOrderByField} from "../../../types";
import {GetMinHeightReturnValue} from "../../../dashboard-widget.config";
import DashboardWidgetFooter from "../../dashboard-widget/footer";

// Interfaces
export interface DataPoint {
    [key: string]: string | number;  // Allow any key-value pairs
}

export interface YAxisConfig {
    id: string;
    position: 'left' | 'top' | 'right' | 'bottom' | 'center'
    beginAtZero?: boolean;
    axis?: 'x' | 'y'; // Axis type, can be 'x' or 'y'
}

export interface DataLabelConfig {
    enabled?: boolean;
    format?: (value: string | number) => string;
    color?: (value: string | number, context: Context, pageNumber: number) => string;
    align?: Align;
    anchor?: Anchor;
    textAlign?: TextAlign;
    offset?: number;
}

export interface BarConfig {
    dataKey: string;        // The key in the data object to plot
    label: string;          // Label for the legend
    color: string | ((count: number, pageNumber: number) => string[]); // Bar color
    stack?: string;        // Optional stack group
    yAxisId?: string;      // Optional y-axis ID for multi-axis charts
    dataLabelConfig?: DataLabelConfig;  // Data label configuration
}

export interface TooltipConfig {
    key: string;           // The key in the data object to display
    label: string;         // Label to show in tooltip
    forBar?: string | string[]; // Optional bar(s) to filter tooltip for
    format?: (value: number) => string;  // Optional formatter
}

export interface FiltersConfig {
    enabled: boolean;
    onClick?: (key: string, point: DataPoint) => void;
}

export interface BarChartConfig {
    xAxisKey?: string; // Key for x-axis values
    xAxisKeys?: string[]; // Optional multiple keys for x-axis values
    sortByKey?: string; // Key to sort by
    sortByKeys?: string[]; // Optional multiple keys to sort by
    sortBy?: (a: DataPoint[keyof DataPoint], b: DataPoint[keyof DataPoint]) => number; // Sorting function
    multiSortBy?: (a: DataPoint, b: DataPoint) => number; // Multi-field sorting function
    indexAxis?: "x" | "y"; // Axis orientation
    interactionMode?: InteractionMode;
    xAxisFormatter?: (...value: (string | number)[]) => string;  // Optional formatter for x-axis values
    yAxes?: YAxisConfig[];               // Configuration for y-axes
    bars: BarConfig[];                  // Bars to plot
    tooltipConfig: TooltipConfig[];     // Tooltip configuration
    stacked?: boolean;                 // Whether to stack bars
    filtersConfig?: FiltersConfig;     // Filters interaction configuration
    dataLabelConfig?: DataLabelConfig;  // Data label configuration
}

interface BarChartProps {
    data: DataPoint[];           // Array of data points
    config: BarChartConfig;     // Chart configuration - now required
    minHeight: GetMinHeightReturnValue,
    isLoading: boolean;
    paginationInfo?: SearchDataSettingsPaginationInfo;
    setPaginationInfo: React.Dispatch<React.SetStateAction<SearchDataSettingsPaginationInfo | undefined>>;
    orderBy?: SearchDataSettingsOrderBy;
    setOrderBy?: React.Dispatch<React.SetStateAction<SearchDataSettingsOrderBy | undefined>>;
    underlyingDataVisibility?: DashboardWidgetLayoutConfig['underlyingDataVisibility'];
    orderByFields?: DashboardWidgetOrderByField[]; // Fields available for ordering
    hasPagination?: boolean;

}

/**
 * Format a value using the provided formatter or return as is
 */
const formatValue = (value: string | number, formatter?: (value: number) => string): string => {
    if (typeof value === 'number' && formatter) {
        return formatter(value);
    }
    return value?.toString() ?? "";
};


export const DashboardBarChartWidget: React.FC<BarChartProps> = ({
                                                                     data,
                                                                     config,
                                                                     minHeight,
                                                                     isLoading,
                                                                     paginationInfo,
                                                                     setPaginationInfo,
                                                                     orderBy,
                                                                     setOrderBy,
                                                                     underlyingDataVisibility,
                                                                     orderByFields = [],
                                                                     hasPagination,
                                                                 }) => {
    const theme = useTheme();
    const {xAxisFormatter, filtersConfig: {onClick: filtersConfigOnClick} = {}, sortBy} = config;

    const sortedData = useMemo(() => {
        if (!!config.sortByKey && !!sortBy && (orderBy?.field === undefined || !orderBy?.field?.length || !orderByFields.length)) {
            return [...data].sort((a, b) => sortBy(a[config.sortByKey!], b[config.sortByKey!]));
        }
        if (!!config.sortByKeys && !!config.multiSortBy && (orderBy?.field === undefined || !orderBy?.field?.length || !orderByFields.length)) {
            return [...data].sort((a, b) => config.multiSortBy!(a, b));
        }
        return data;
    }, [config.multiSortBy, config.sortByKey, config.sortByKeys, data, orderBy?.field, orderByFields.length, sortBy]);

    const prevPageNumber = useRef(paginationInfo?.currentPage);

    /**
     * Store the previous page number when the component unmounts
     */
    useEffect(() => () => {
        prevPageNumber.current = paginationInfo?.currentPage;
    }, [paginationInfo?.currentPage]);

    const pageNumber = useMemo(() => {
        if (isLoading) {
            return prevPageNumber.current ?? 1;
        }
        return paginationInfo?.currentPage ?? 1;
    }, [isLoading, paginationInfo?.currentPage]);

    /**
     * Handle click event on chart
     */
    const handleClick = useCallback((event: ChartEvent, elements: ActiveElement[]) => {
        if (elements.length > 0 && config.filtersConfig?.enabled && filtersConfigOnClick) {
            const index = elements[0].index;
            const point = sortedData[index];
            filtersConfigOnClick(config.xAxisKey ?? config.xAxisKeys?.[0] ?? "", point);
        }
    }, [config.filtersConfig?.enabled, config.xAxisKey, config.xAxisKeys, filtersConfigOnClick, sortedData]);

    const chartData = useMemo<ChartData<'bar', DefaultDataPoint<'bar'>, string>>(() => {
        const xAxisKeys = config.xAxisKeys && config.xAxisKeys.length > 0 ? config.xAxisKeys : (config.xAxisKey ? [config.xAxisKey] : []);
        return ({
            labels: sortedData.map(item => xAxisFormatter?.(...xAxisKeys.map(key => item[key]?.toString())) ?? xAxisKeys.map(key => item[key]?.toString()).join(' ')),
            datasets: config.bars
                .filter(bar => underlyingDataVisibility?.[bar.dataKey] !== false) // Filter out hidden lines
                .map(bar => ({
                    label: bar.label,
                    data: sortedData.map(item => item[bar.dataKey] as number),
                    backgroundColor: typeof bar.color === 'string' ? bar.color : bar.color(sortedData.length, pageNumber),
                    stack: bar.stack,
                    yAxisID: bar.yAxisId,
                    borderRadius: 2,
                    maxBarThickness: 50,
                    datalabels: {
                        display: bar.dataLabelConfig?.enabled ?? false,
                        anchor: bar.dataLabelConfig?.anchor,
                        align: bar.dataLabelConfig?.align,
                        textAlign: bar.dataLabelConfig?.textAlign,
                        offset: bar.dataLabelConfig?.offset,
                        formatter: (value, context) => {
                            const dataPoint = context.chart.data.datasets[context.datasetIndex].data[context.dataIndex] as number;
                            return formatValue(dataPoint ?? "", bar.dataLabelConfig?.format);
                        },
                        color: (context) => {
                            const dataPoint = context.chart.data.datasets[context.datasetIndex].data[context.dataIndex] as number;
                            return bar.dataLabelConfig?.color ? bar.dataLabelConfig.color(dataPoint, context, pageNumber) : theme.palette.text.primary;
                        }
                    }
                }))
        })
    }, [config.xAxisKeys, config.xAxisKey, config.bars, sortedData, xAxisFormatter, underlyingDataVisibility, pageNumber, theme.palette.text.primary]);

    const options = useMemo<ChartOptions<'bar'>>(() => ({
        responsive: true,
        maintainAspectRatio: false,
        onClick: handleClick,
        indexAxis: config.indexAxis ?? 'x',
        interaction: {
            mode: config.interactionMode ?? 'index' as const,
            intersect: false,
        },
        borderColor: theme.palette.divider,
        onHover: (event, chartElement) => {
            if (chartElement && event?.native?.target && config.filtersConfig?.enabled) {
                (event.native.target as any).style?.setProperty?.('cursor', 'pointer');
            }
        },
        plugins: {
            legend: {
                position: 'top' as const,
                align: 'start' as const,
                labels: {
                    usePointStyle: true,
                },
                display: config.bars.filter(bar => underlyingDataVisibility?.[bar.dataKey] !== false).length > 1,
            },
            tooltip: {
                callbacks: {
                    title: (items: TooltipItem<'bar'>[]) => {
                        return xAxisFormatter ? xAxisFormatter(items[0].label) : items[0].label;
                    },
                    label: (context: TooltipItem<'bar'>) => {
                        // include the barFor which the tooltip is shown
                        return [
                            ...config.tooltipConfig
                                .filter(item => !item.forBar || (typeof item.forBar === 'string' ? item.forBar === context.dataset.label : item.forBar.includes(context.dataset.label ?? "")))
                                .map(config => {
                                    const rawValue = sortedData[context.dataIndex][config.key] as number;
                                    const formattedValue = formatValue(rawValue, config.format);
                                    return `${config.label}: ${formattedValue}`;
                                }),
                        ];
                    }
                }
            },
            datalabels: {
                display: config.dataLabelConfig?.enabled ?? false,
                anchor: config.dataLabelConfig?.anchor,
                align: config.dataLabelConfig?.align,
                textAlign: config.dataLabelConfig?.textAlign,
                offset: config.dataLabelConfig?.offset,
                padding: 8,
                formatter: (value, context) => {
                    const dataPoint = context.chart.data.datasets[context.datasetIndex].data[context.dataIndex] as number;
                    return formatValue(dataPoint ?? "", config.dataLabelConfig?.format);
                },
                color: (context) => {
                    const dataPoint = context.chart.data.datasets[context.datasetIndex].data[context.dataIndex] as number;
                    return config.dataLabelConfig?.color ? config.dataLabelConfig.color(dataPoint, context, pageNumber) : theme.palette.text.primary;
                }
            }
        },
        scales: {
            x: {
                grid: {
                    color: theme.palette.mode === 'light' ? '#f3f3f3' : '#454545',
                },
                stacked: config.stacked,
                ticks: {
                    color: theme.palette.text.primary,
                    format: {compactDisplay: 'short', currencyDisplay: 'symbol', notation: 'compact'},
                },
            },
            ...config.yAxes
                ?.filter(yAxis => underlyingDataVisibility?.[yAxis.id] !== false) // Filter out hidden y-axes
                ?.reduce((acc, yAxis) => ({
                    ...acc,
                    [yAxis.id]: {
                        axis: yAxis.axis,
                        beginAtZero: yAxis?.beginAtZero ?? true,
                        type: 'linear' as const,
                        stacked: config.stacked,
                        display: true,
                        position: yAxis.position,
                        grid: {
                            drawOnChartArea: yAxis.position === 'left',
                            color: theme.palette.mode === 'light' ? '#f3f3f3' : '#454545',
                        },
                        ticks: {
                            color: theme.palette.text.primary,
                            format: {compactDisplay: 'short', currencyDisplay: 'symbol', notation: 'compact'},
                        }
                    } as DeepPartial<ScaleTypeRegistry['linear']['options']>,
                }), {}) ?? {},
        }
    }), [handleClick, config.indexAxis, config.interactionMode, config.bars, config.dataLabelConfig, config.stacked, config.yAxes, config.filtersConfig?.enabled, config.tooltipConfig, theme.palette.divider, theme.palette.mode, theme.palette.text.primary, underlyingDataVisibility, xAxisFormatter, sortedData, pageNumber]);

    return (
        <>
            <Stack direction={"column"} gap={0.5}>
                <Box sx={{width: '100%', height: '100%', minHeight: minHeight}}>
                    <Bar data={chartData} options={options}/>
                </Box>
                <DashboardWidgetFooter
                    paginationInfo={paginationInfo}
                    setPaginationInfo={setPaginationInfo}
                    isLoading={isLoading}
                    orderBy={orderBy}
                    setOrderBy={setOrderBy}
                    orderByFields={orderByFields}
                    hasPagination={hasPagination}
                    data={data}
                />
            </Stack>
        </>
    );
};

export default DashboardBarChartWidget;